import * as React from 'react';

import { composeRefs } from './composeRefs';
import { mergeProps } from './mergeProps';

export interface SlotProps extends React.HTMLAttributes<HTMLElement> {
  children: React.ReactNode;
}

/**
 * Renders its single child, merging all props (className, style, event handlers, ref)
 * from the Slot onto the child. If multiple children, warns in dev.
 */
export const Slot = React.forwardRef<HTMLElement, SlotProps>((props, ref) => {
  const { children, ...slotProps } = props;

  const childArray = React.Children.toArray(children).filter((c) => c != null && c !== false);
  if (childArray.length !== 1 || !React.isValidElement(childArray[0])) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('Slot: expected a single React element child');
    }
    return null;
  }

  const child = childArray[0];
  const merged = mergeProps(slotProps as Record<string, unknown>, child.props as Record<string, unknown>);
  const childRef = (child as React.ReactElement & { ref?: React.Ref<HTMLElement | null> }).ref;

  return React.cloneElement(child, {
    ...merged,
    ref: composeRefs(ref, childRef),
  } as typeof child.props);
});

Slot.displayName = 'Slot';

export interface SlottableProps {
  children: React.ReactNode;
}

/**
 * Marker wrapper for compound components when only part of the tree should receive merged slot props.
 */
export const Slottable: React.FC<SlottableProps> = ({ children }) => <>{children}</>;
Slottable.displayName = 'Slottable';
